import _ from 'lodash'
import {Box, ColumnLayout, Container, Grid, Header, Popover, SpaceBetween, Table, Tabs} from '@amzn/awsui-components-react';
import ExpandableSection from '@amzn/awsui-components-react/polaris/expandable-section';
import React, {useEffect, useState } from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {Rule, RuleGroup} from '../../../backend/model';
import SyntaxHighlighter from 'react-syntax-highlighter';
import {TableDetails, TableInput, operators, useTableCreation } from '../../TableTemplate';
import { ExclusionGroupLink, RuleGroupLink, RuleLink } from '../../TableItemLink';
import AjaxStatusIndicator from '../../basic/AjaxStatusIndicator';
import {formatJson, prettifyJSON} from '../../../utils/jsonHelper';
import { PropertyList } from '../../PropertyList';
import { InheritedPropertyPopover } from '../InheritedPropertyPopover';

interface RuleGroupDetailsPanelProps extends RouteComponentProps {
    childRuleGroups: string[],
    inheritedRuleGroups: RuleGroup[],
    ruleGroup: RuleGroup,
    ruleName: string,
}

const RuleGroupDetailsPanel: React.FC<RuleGroupDetailsPanelProps> = (props) => {
    const [rules, setRules] = useState<Rule[]>()
    const [rule, setRule] = useState<Rule>()
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const ruleGroup = props.ruleGroup
    const ruleGroupLineage = _.concat(props.inheritedRuleGroups, [ruleGroup])
    const ruleNameFromUrl = props.ruleName

    const getAttribute = (attribute: string): Map<string, string> => {
        const values: Map<string, string> = new Map() // key: attribute value, value: rule group inherited from (or self)

        if (!Array.isArray(_.get(ruleGroupLineage[0], attribute))) throw new Error(`${attribute} is not an array type in RuleGroup.`)

            ruleGroupLineage.forEach(ruleGroup => {
                _.get(ruleGroup, attribute)?.forEach((value: string) => {
                    values.set(value, ruleGroup.name)
                }
            )
        })

        return values
    }

    const getApprovers = (): (string | JSX.Element)[] => {
        const approversMap = getAttribute("approvers")
        return Array.from(approversMap).map(([value, ruleGroupName]) => (
            ruleGroupName == ruleGroup.name ? `${value}` : <InheritedPropertyPopover parentRuleGroup={ruleGroupName}>{value}</InheritedPropertyPopover>
        ))
    }

    const getExclusionGroups = (): JSX.Element[] => {
        const exclusionGroupsMap = getAttribute("exclusionGroups")
        return Array.from(exclusionGroupsMap).map(([value, ruleGroupName]) => (
            ruleGroupName == ruleGroup.name ? <ExclusionGroupLink exclusionGroupName={value} /> : <InheritedPropertyPopover parentRuleGroup={ruleGroupName}>
                <ExclusionGroupLink exclusionGroupName={value} />
            </InheritedPropertyPopover>
        ))
    }

    const getChildren = (): JSX.Element[] => props.childRuleGroups.map(ruleGroupName => <RuleGroupLink ruleGroupName={ruleGroupName}/>)

    const getOwner = (): string => props.inheritedRuleGroups.at(0)?.businessIdentity ?? ruleGroup.businessIdentity

    const resetState = (): void => {
        setRules(undefined)
        setRule(undefined)
        setErrorMessages([])
    }

    useEffect(() => {
        getRule(ruleNameFromUrl)
    }, [props, ruleNameFromUrl])

    const getRule = (ruleName: string) => {
        resetState()

        setIsLoading(true)
        if (ruleName) {
            const ruleFound = ruleGroup.rules!.find((rule) => rule.name == ruleName)
            if (ruleFound == undefined) {
                setErrorMessages(() => [`No rule with name ${ruleName}`])
            } else {
                setRule(ruleFound)
            }
        } else {
            setRules(() => ruleGroup.rules)
        }
        setIsLoading(false)
    }
    
    const fcSelectorSection = (fcSelectorRuleGroup: RuleGroup): JSX.Element => {
        const title = fcSelectorRuleGroup.name == ruleGroup.name ? "Rule Group FC Selector" : `FC Selector Inherited From ${fcSelectorRuleGroup.name}`

        return (
            <React.Fragment key={`fcSelector-${fcSelectorRuleGroup.name}`}>
                <ExpandableSection className='ruleGroupFcSelectorPanel' variant='container' header={<Header variant='h2'>{title}</Header>} defaultExpanded>
                    <SyntaxHighlighter id='whdp-rules' customStyle={{background: 'transparent'}} language="json">
                        {formatJson(fcSelectorRuleGroup.fcSelector ?? "null", 0, 2)}
                    </SyntaxHighlighter>
                </ExpandableSection>
                <br/>
                <br/>
            </React.Fragment>
        )
    }

    const summary = (
        <ColumnLayout columns={5} minColumnWidth={200} variant="text-grid">
            <SpaceBetween size="l">
                <div data-test-id="rule-group-summary-name">
                    <Box variant="awsui-key-label">Name</Box>
                    <div>{ruleGroup.name}</div>
                </div>
            </SpaceBetween>
            <SpaceBetween size="l">
                <div data-test-id="rule-group-summary-approvers">
                    <Box variant="awsui-key-label">Approvers</Box>
                    <PropertyList items={getApprovers()} />
                </div>
            </SpaceBetween>
            <SpaceBetween size="l">
                <div data-test-id="rule-group-summary-exclusion-groups">
                    <Box variant="awsui-key-label">Exclusion Groups</Box>
                    <PropertyList items={getExclusionGroups()} />
                </div>
            </SpaceBetween>
            <SpaceBetween size="l">
                <div data-test-id="rule-group-summary-children">
                    <Box variant="awsui-key-label">Child Rule Groups</Box>
                    <PropertyList items={getChildren()} />
                </div>
            </SpaceBetween>
            <SpaceBetween size="l">
                <div data-test-id="rule-group-summary-owner">
                    <Box variant="awsui-key-label">Owner</Box>
                    <div>{getOwner()}</div>
                </div>
            </SpaceBetween>
        </ColumnLayout>
    )

    const TABLE_ID = 'rulesTable'

    const COLUMN_DEFINITIONS = [
        {
            id: 'name',
            header: 'Name',
            cell: (rule: Rule) => <RuleLink ruleGroupName={ruleGroup.name} ruleName={rule.name} />,
            sortingField: "name",
        },
    ]

    const FILTERING_PROPERTIES = [
        {
            propertyLabel: 'Name',
            key: 'name',
            groupValuesLabel: 'Rule Name',
            operators: operators,
        },
    ]

    const RuleGroupsTable: React.FC<TableInput> = ({tableItems, fileName}) => {
        const tableDetails: TableDetails = {
            tableId: TABLE_ID,
            header: '',
            fileName: fileName,
            getRow: function (row: any): string[] {
                return [row.name];
            },
        };
        return useTableCreation(tableDetails, COLUMN_DEFINITIONS, tableItems, FILTERING_PROPERTIES);
    }

    const gridDef = Array(4).fill([
        {colspan: {default: 12, xxs: 2}},
        {colspan: {default: 12, xxs: 10}},
    ]).flat()

    return (
        <div>
            <AjaxStatusIndicator errorMessage={errorMessages.join(' ')} loading={isLoading}/>
            <ExpandableSection
                id='ruleGroupSummaryPanel'
                variant='container'
                header={<Header variant='h2'>Rule Group Summary</Header>}
                defaultExpanded>
                {summary}
            </ExpandableSection>
            <br/>
            <br/>
            { ruleGroupLineage.filter(ruleGroup => ruleGroup.fcSelector).map(ruleGroup => fcSelectorSection(ruleGroup)) }
            {rule &&
                <ExpandableSection
                    id='ruleGroupRulesPanel'
                    variant='container'
                    header={<Header variant='h2'>Rule</Header>}
                    defaultExpanded>
                    <>
                        <Grid
                            gridDefinition={gridDef}>
                            <Box variant='p'>Name: </Box>
                            <Box variant='p' id='whdp-name'>{rule.name}</Box>

                            <Box variant='p'>Override Selector: </Box>
                            <SyntaxHighlighter id='whdp-overrideSelector'
                                               customStyle={{background: 'transparent'}}
                                               language="json">
                                {formatJson(rule?.overrideSelector!, 0, 2)}
                            </SyntaxHighlighter>

                            <Box variant='p'>FC Selector: </Box>
                            <SyntaxHighlighter id='whdp-fcSelector'
                                               customStyle={{background: 'transparent'}}
                                               language="json">
                                {formatJson(rule?.fcSelector!, 0, 2)}
                            </SyntaxHighlighter>

                            <Box variant='p'>Mappings: </Box>
                            <SyntaxHighlighter id='whdp-mappings'
                                               customStyle={{background: 'transparent'}}
                                               language="json">
                                {prettifyJSON(rule?.mappings!)}
                            </SyntaxHighlighter>
                        </Grid>
                    </>
                </ExpandableSection>
            }
            {rules &&
                <ExpandableSection
                    id='ruleGroupRulesPanel'
                    variant='container'
                    header={<Header variant='h2'>Rules</Header>}
                    defaultExpanded>
                    <RuleGroupsTable tableItems={rules?? []} fileName='DNC rules' />
                </ExpandableSection>
            }
        </div>
    );
};

export default RuleGroupDetailsPanel;
